import { useEffect, useState } from "react";
import { PageHeader } from "../App";
import { 
  Robot, 
  CheckCircle, 
  XCircle,
  Spinner,
  ArrowsClockwise,
  Plugs
} from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import api from "../lib/api";

export default function Settings() {
  const [ollamaStatus, setOllamaStatus] = useState({
    connected: false,
    models: [],
    config: {
      base_url: "http://localhost:11434",
      model: "llama3.2",
      enabled: false
    }
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState({
    base_url: "http://localhost:11434",
    model: "llama3.2",
    enabled: false
  });

  useEffect(() => {
    checkOllamaStatus();
  }, []);

  const checkOllamaStatus = async () => {
    setLoading(true);
    try {
      const status = await api.getOllamaStatus();
      setOllamaStatus(status);
      setConfig(status.config);
    } catch (error) {
      console.error("Failed to check Ollama status:", error);
    } finally {
      setLoading(false);
    }
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      await api.updateOllamaConfig(config);
      toast.success("Settings saved successfully");
      checkOllamaStatus();
    } catch (error) {
      console.error("Failed to save config:", error);
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <PageHeader 
        title="Settings" 
        subtitle="Configure Ollama integration and preferences"
      />
      
      <div className="p-8 space-y-6">
        {/* Ollama Connection */}
        <div className="chart-container">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                ollamaStatus.connected ? 'bg-emerald-500/10' : 'bg-zinc-800'
              }`}>
                <Robot weight="duotone" className={`w-6 h-6 ${
                  ollamaStatus.connected ? 'text-emerald-400' : 'text-zinc-400'
                }`} />
              </div>
              <div>
                <h3 className="text-lg font-medium text-zinc-100">Ollama Integration</h3>
                <p className="text-sm text-zinc-500">Local AI for categorization and analysis</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              {loading ? (
                <Spinner weight="bold" className="w-5 h-5 text-zinc-400 animate-spin" />
              ) : ollamaStatus.connected ? (
                <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald-500/10 rounded-full">
                  <CheckCircle weight="fill" className="w-4 h-4 text-emerald-400" />
                  <span className="text-sm text-emerald-400">Connected</span>
                </div>
              ) : (
                <div className="flex items-center gap-2 px-3 py-1.5 bg-zinc-800 rounded-full">
                  <XCircle weight="fill" className="w-4 h-4 text-zinc-500" />
                  <span className="text-sm text-zinc-500">Not Connected</span>
                </div>
              )}
              
              <Button
                variant="outline"
                size="sm"
                onClick={checkOllamaStatus}
                disabled={loading}
                data-testid="refresh-status-btn"
              >
                <ArrowsClockwise weight="bold" className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          </div>

          {/* Connection Status Info */}
          {!ollamaStatus.connected && (
            <div className="mb-6 p-4 bg-amber-500/10 border border-amber-500/30 rounded-md">
              <p className="text-sm text-amber-300 mb-2">Ollama not detected</p>
              <p className="text-xs text-amber-400/70">
                Make sure Ollama is installed and running locally. 
                Install from <a href="https://ollama.ai" target="_blank" rel="noopener noreferrer" className="underline">ollama.ai</a>
              </p>
            </div>
          )}

          {/* Configuration */}
          <div className="space-y-6">
            <div className="flex items-center justify-between p-4 bg-zinc-900/50 rounded-md">
              <div>
                <Label className="text-zinc-200">Enable Ollama Features</Label>
                <p className="text-xs text-zinc-500 mt-1">
                  Use local AI for conversation analysis and categorization
                </p>
              </div>
              <Switch
                checked={config.enabled}
                onCheckedChange={(checked) => setConfig({ ...config, enabled: checked })}
                data-testid="ollama-enabled-toggle"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="base-url" className="text-zinc-400">Base URL</Label>
                <Input
                  id="base-url"
                  value={config.base_url}
                  onChange={(e) => setConfig({ ...config, base_url: e.target.value })}
                  placeholder="http://localhost:11434"
                  className="bg-zinc-900 border-zinc-800"
                  data-testid="ollama-base-url"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="model" className="text-zinc-400">Model</Label>
                {ollamaStatus.connected && ollamaStatus.models.length > 0 ? (
                  <Select 
                    value={config.model} 
                    onValueChange={(value) => setConfig({ ...config, model: value })}
                  >
                    <SelectTrigger className="bg-zinc-900 border-zinc-800" data-testid="ollama-model-select">
                      <SelectValue placeholder="Select model" />
                    </SelectTrigger>
                    <SelectContent>
                      {ollamaStatus.models.map(model => (
                        <SelectItem key={model} value={model}>{model}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    id="model"
                    value={config.model}
                    onChange={(e) => setConfig({ ...config, model: e.target.value })}
                    placeholder="llama3.2"
                    className="bg-zinc-900 border-zinc-800"
                    data-testid="ollama-model-input"
                  />
                )}
              </div>
            </div>

            <div className="flex justify-end">
              <Button
                onClick={saveConfig}
                disabled={saving}
                data-testid="save-settings-btn"
              >
                {saving ? (
                  <>
                    <Spinner weight="bold" className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save Settings'
                )}
              </Button>
            </div>
          </div>
        </div>

        {/* Available Models */}
        {ollamaStatus.connected && ollamaStatus.models.length > 0 && (
          <div className="chart-container">
            <h3 className="text-sm font-medium text-zinc-400 mb-4 uppercase tracking-wider">
              Available Models
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
              {ollamaStatus.models.map(model => (
                <div 
                  key={model}
                  className={`p-3 rounded-md border ${
                    config.model === model 
                      ? 'border-blue-500 bg-blue-500/10' 
                      : 'border-zinc-800 bg-zinc-900/30'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <Plugs weight="regular" className={`w-4 h-4 ${
                      config.model === model ? 'text-blue-400' : 'text-zinc-500'
                    }`} />
                    <span className={`text-sm truncate ${
                      config.model === model ? 'text-blue-300' : 'text-zinc-300'
                    }`}>
                      {model}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Feature Info */}
        <div className="chart-container">
          <h3 className="text-sm font-medium text-zinc-400 mb-4 uppercase tracking-wider">
            Ollama Features
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 bg-zinc-900/30 rounded-md">
              <p className="text-sm text-zinc-200 mb-2">Conversation Categorization</p>
              <p className="text-xs text-zinc-500">
                Automatically categorize conversations by topic using local AI.
                Works entirely offline.
              </p>
            </div>
            <div className="p-4 bg-zinc-900/30 rounded-md">
              <p className="text-sm text-zinc-200 mb-2">Quality Scoring</p>
              <p className="text-xs text-zinc-500">
                Score conversations by quality and relevance for training data selection.
              </p>
            </div>
            <div className="p-4 bg-zinc-900/30 rounded-md">
              <p className="text-sm text-zinc-200 mb-2">Theme Extraction</p>
              <p className="text-xs text-zinc-500">
                Extract key themes and topics from your conversation datasets.
              </p>
            </div>
            <div className="p-4 bg-zinc-900/30 rounded-md">
              <p className="text-sm text-zinc-200 mb-2">Privacy First</p>
              <p className="text-xs text-zinc-500">
                All processing happens locally. Your data never leaves your machine.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
